import _ from "lodash";

/**
 * Key that we use to store stripe subscription status in the user details
 * column.
 *
 */
export const UserDetailsStripeSubscriptionStatusKey =
  "stripeSubscriptionStatus";
export const UserDetailsStripeSubscriptionId = "stripeSubscriptionId";
export const UserDetailsStripeCheckoutId = "stripeCheckoutId";

export function getUserSubscriptionStatus<T extends { details: object }>(
  user: T
): string | null {
  const value = _.get(user.details, UserDetailsStripeSubscriptionStatusKey);
  if (!_.isString(value)) {
    return null;
  }

  return value;
}

export function getUserSubscriptionId<T extends { details: object }>(
  user: T
): string | null {
  const value = _.get(user.details, UserDetailsStripeSubscriptionId);

  if (!_.isString(value)) {
    return null;
  }

  return value;
}

export function getUserStripeCheckoutSessionId<T extends { details: object }>(
  user: T
): string | null {
  const value = _.get(user.details, UserDetailsStripeCheckoutId);

  if (!_.isString(value)) {
    return null;
  }

  return value;
}

export const FULL_PRICE_PER_MONTH_IN_CENTS = 4_000;
export const DISCOUNTED_PRICE_PER_MONTH_IN_CENTS = 2_999;
export const FIFTY_OFF_DISCOUNTED_PRICE_PER_MONTH_IN_CENTS = 2_000;
export const MEMBERSHIP_YEARLY_PAUSE_LIMIT = 2;

export const StripeSubscriptionProperties = {
  Status: UserDetailsStripeSubscriptionStatusKey,
  SubscriptionId: UserDetailsStripeSubscriptionId,
  CheckoutId: UserDetailsStripeCheckoutId,
  PackageName: "package_name",
  NumCredits: "num_credits",
  CreditValue: "credit_value",
  CurrentCycleStart: "current_cycle_start",
  CurrentCycleEnd: "current_cycle_end",
  CancelAtPeriodEnd: "cancel_at_period_end",
  AmountCents: "amount_cents",
} as const;

export interface SubscriptionProperties {
  [StripeSubscriptionProperties.Status]: string;
  [StripeSubscriptionProperties.SubscriptionId]: string;
  [StripeSubscriptionProperties.CheckoutId]: string;
  [StripeSubscriptionProperties.PackageName]: string;
  [StripeSubscriptionProperties.NumCredits]: number;
  [StripeSubscriptionProperties.CreditValue]: number;
  [StripeSubscriptionProperties.CurrentCycleStart]: Date;
  [StripeSubscriptionProperties.CurrentCycleEnd]: Date;
  [StripeSubscriptionProperties.CancelAtPeriodEnd]: boolean;
  [StripeSubscriptionProperties.AmountCents]: number;
}

export const subscriptionPackagePlans = [
  {
    title: "Basic",
    badgeCopy: null,
    cardColor: "info",
    centsPerMonth: 6000,
    numCredits: 129,
    creditsCopy: "Roughly 2 group sessions",
    features: [
      { text: "Add-ons credits", enabled: true },
      { text: "Concierge (chat only)", enabled: true },
      { text: "Credit rollover", enabled: true },
      { text: "Fixed-length courses", enabled: false },
      { text: "Scheduling assistant", enabled: false },
    ],
  },
  {
    title: "Silver",
    badgeCopy: "Most Popular",
    cardColor: "error",
    centsPerMonth: 12000,
    numCredits: 264,
    creditsCopy: "Roughly 5 group sessions or 2 tutoring sessions",
    features: [
      { text: "Add-ons credits", enabled: true },
      { text: "Scheduling assistant", enabled: true },
      { text: "Credit rollover", enabled: true },
      { text: "Concierge (chat only)", enabled: true },
    ],
  },
  {
    title: "Gold",
    badgeCopy: null,
    cardColor: "primary",
    centsPerMonth: 24000,
    numCredits: 536,
    creditsCopy: "Roughly 10 group sessions or 4 tutoring sessions",
    features: [
      { text: "Add-ons credits", enabled: true },
      { text: "Scheduling assistant", enabled: true },
      { text: "Credit rollover", enabled: true },
      { text: "Unlimited concierge support", enabled: true },
    ],
  },
  {
    title: "Premium",
    badgeCopy: "Great for Families",
    cardColor: "secondary",
    centsPerMonth: 60000,
    numCredits: 1380,
    creditsCopy: "Roughly 26 group sessions or 10 tutoring sessions",
    features: [
      { text: "Add-ons credits", enabled: true },
      { text: "Scheduling assistant", enabled: true },
      { text: "Credit rollover", enabled: true },
      { text: "Unlimited concierge support", enabled: true },
    ],
  },
];

export * from "./email";
